import React, { useState } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { FooterDisclaimer } from './FooterDisclaimer';
import { CookieConsent } from './CookieConsent';
import { MARKET_PROFILES, PRIMARY_MARKET_CODES } from '../data/localization';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/guides?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const tools = [
    { name: 'Compound Interest', path: '/tools/compound-interest' },
    { name: 'Debt-to-Income', path: '/tools/debt-to-income' },
  ];
  const primaryMarkets = PRIMARY_MARKET_CODES.map((code) => MARKET_PROFILES[code]);
  const europeCountryMarkets = [MARKET_PROFILES.DE, MARKET_PROFILES.FR, MARKET_PROFILES.ES];

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <div className="w-8 h-8 bg-slate-900 flex items-center justify-center rounded-sm mr-2 group-hover:scale-105 transition-transform">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="text-xl font-semibold tracking-tight text-slate-900">
                FinancialMetrics<span className="text-slate-400">.report</span>
              </span>
            </Link>
            
            <div className="hidden lg:ml-10 lg:flex lg:space-x-8">
              <NavLink 
                to="/tools" 
                className={({ isActive }) => 
                  `inline-flex items-center px-1 pt-1 text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Calculators
              </NavLink>
              
              <NavLink 
                to="/guides" 
                className={({ isActive }) => 
                  `inline-flex items-center px-1 pt-1 text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Guides
              </NavLink>

              <NavLink
                to="/topics"
                className={({ isActive }) =>
                  `inline-flex items-center px-1 pt-1 text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Topics
              </NavLink>

              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `inline-flex items-center px-1 pt-1 text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                About
              </NavLink>

              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `inline-flex items-center px-1 pt-1 text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Contact
              </NavLink>
              
              <div 
                className="relative flex items-center group"
                onMouseEnter={() => setIsToolsOpen(true)}
                onMouseLeave={() => setIsToolsOpen(false)}
              >
                 <button 
                   onClick={() => setIsToolsOpen(!isToolsOpen)}
                   aria-label="Open jurisdiction menu"
                   className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center hover:text-slate-900 transition-colors focus:outline-none"
                 >
                    Markets <ChevronDown className={`ml-1 h-3 w-3 transition-transform ${isToolsOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {isToolsOpen && (
                   <div className="absolute top-full left-0 z-50 mt-2 w-56 rounded-xl border border-slate-200 bg-white py-3 shadow-2xl">
                      <Link to="/tools" onClick={() => setIsToolsOpen(false)} className="block px-6 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors">All Calculators</Link>
                      {primaryMarkets.map((market) => (
                        <Link key={market.code} to={market.routePrefix} onClick={() => setIsToolsOpen(false)} className="block px-6 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors">
                          {market.navLabel}
                        </Link>
                      ))}
                      <div className="mx-6 my-2 border-t border-slate-100" />
                      {europeCountryMarkets.map((market) => (
                        <Link key={market.code} to={market.routePrefix} onClick={() => setIsToolsOpen(false)} className="block px-6 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-colors">
                          {market.navLabel}
                        </Link>
                      ))}
                   </div>
                 )}
              </div>
            </div>
          </div>

          <div className="hidden lg:flex lg:items-center lg:ml-6 space-x-4">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-48 pl-4 pr-10 py-1.5 border-none rounded-full text-xs bg-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 focus:w-56 transition-all"
                placeholder="Search guides..."
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Search className="h-3 w-3 text-slate-400" />
              </div>
            </form>

          </div>

          <div className="-mr-2 flex items-center lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
          <div className="lg:hidden overflow-hidden">
            <div className="pt-2 pb-3 space-y-1">
              <NavLink
                to="/guides"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => 
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Guides
              </NavLink>
              <NavLink
                to="/tools"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => 
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Calculators
              </NavLink>
              <NavLink
                to="/topics"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Topics
              </NavLink>
              <NavLink
                to="/about"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                About
              </NavLink>
              <NavLink
                to="/contact"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Contact
              </NavLink>
              <NavLink
                to="/privacy-policy"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Privacy Policy
              </NavLink>
              {primaryMarkets.map((market) => (
                <NavLink
                  key={market.code}
                  to={market.routePrefix}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                  }
                >
                  {market.navLabel}
                </NavLink>
              ))}
              <NavLink
                to="/cookie-policy"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-slate-50 border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-700'}`
                }
              >
                Cookie Policy
              </NavLink>
            </div>
          </div>
        )}
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white border-t border-slate-200 mt-auto">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center text-[10px] font-medium text-slate-400 uppercase tracking-widest gap-6 text-center md:text-left">
          <span>&copy; {new Date().getFullYear()} FinancialMetrics.report</span>
          <div className="flex flex-wrap justify-center space-x-4 md:space-x-8">
            <span className="hover:text-slate-600 transition-colors cursor-default">Pure Guides</span>
            <span className="text-slate-900 border-x border-slate-200 px-4 md:px-8">Educational Purposes Only</span>
            <span className="hover:text-slate-600 transition-colors cursor-default">North America & Europe Focus</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {!isAdminPath && <Navbar />}
      <main className="flex-grow">
        {children}
      </main>
      {!isAdminPath && <FooterDisclaimer />}
      {!isAdminPath && <CookieConsent />}
    </div>
  );
};
